import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, CheckCircle2, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useIsAuthenticated } from '@/core/auth/SessionStore'
import { PickerSheet } from '@/features/common/PickerSheet'
import type { ObraParteDto, ServicoParteDto } from '@/types/parteDiariaDtos'
import {
  contextoEpp,
  diferencaLinha,
  errosDaLinha,
  errosEpp,
  formularioMedicao,
  horimetroInicial,
  iniciosDasLinhas,
  lancamentoCompleto,
  papelDaLinha,
  previaValorEpp,
  rotuloMve,
  useParteDiariaStore,
  type LinhaMveDraft,
} from '@/features/parteDiaria/parteDiariaStore'
import { LinhaMveFormSheet } from '@/features/parteDiaria/LinhaMveFormSheet'
import { useObrasParteDiaria, useServicosParteDiaria } from '@/features/parteDiaria/useParteDiaria'
import { dataBR, diasInclusivos, type CampoEpp } from '@/features/parteDiaria/parteDiariaRegras'
import { Alerta, LabeledInput, PickerField, Section } from '@/features/parteDiaria/fields'
import { fmtBRL, fmtNum } from '@/features/parteDiaria/format'

/**
 * Passo 1: MVE → linhas de hoje (principal + frações); EPP → um período.
 */
export function ParteDiariaStep1Route() {
  const authed = useIsAuthenticated()
  const navigate = useNavigate()
  const store = useParteDiariaStore()

  if (!authed) return <Navigate to="/login" replace />
  if (store.mveId == null || store.modulo == null) return <Navigate to="/parte-diaria/step0" replace />

  const completo = lancamentoCompleto(store)

  return (
    <main className="min-h-svh bg-bg-base flex flex-col">
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border pt-safe pl-safe pr-safe">
        <div className="flex items-center gap-2 px-2 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/parte-diaria/step0')} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-on-surface">
              Parte Diária · 2 de 3 · {store.modulo === 'MVE' ? 'Linhas do dia' : 'Período'}
            </h1>
            <p className="text-xs text-on-surface-dim truncate">
              {rotuloMve(store)} · {dataBR(store.dt)}
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-w-2xl mx-auto w-full pb-28">
        {store.rejeicao && (
          <Alerta tone="bad">
            <span className="block font-medium">Recusada pelo servidor</span>
            <span className="block">{store.rejeicao.message}</span>
          </Alerta>
        )}
        {store.contexto == null && (
          <Alerta tone="warn">
            {horimetroInicial(store) != null
              ? `Sem internet: o horímetro inicial (${fmtNum(horimetroInicial(store), 0)}) é o último conhecido${store.apoio?.referencia ? ` em ${dataBR(store.apoio.referencia)}` : ''}. O SIGA confere no envio.`
              : 'Sem internet e sem horímetro conhecido deste equipamento. O início será definido pelo SIGA no envio.'}
          </Alerta>
        )}
        {(store.apoio?.pendentesHoje ?? 0) > 0 && (
          <Alerta tone="warn">
            Já há {store.apoio!.pendentesHoje} linha(s) deste equipamento nesta data aguardando envio neste aparelho — as novas entram como frações a partir delas.
          </Alerta>
        )}
        {store.modulo === 'MVE' ? <LinhasMve /> : <PeriodoEpp />}
      </div>

      <div className="sticky bottom-0 bg-bg-base/95 backdrop-blur border-t border-border p-4 pb-safe-plus-4 pl-safe pr-safe">
        <Button variant="primary" size="lg" disabled={!completo} onClick={() => navigate('/parte-diaria/step2')} className="w-full">
          Revisar
        </Button>
      </div>
    </main>
  )
}

// ============================================================================
//  MVE — lista de linhas do dia
// ============================================================================

function LinhasMve() {
  const store = useParteDiariaStore()
  const [editando, setEditando] = useState<{ linha: LinhaMveDraft; indice: number } | null>(null)
  const formulario = formularioMedicao(store)
  const inicios = iniciosDasLinhas(store)
  const ultima = store.linhas[store.linhas.length - 1]
  const podeFracionar = store.linhas.length === 0 || (ultima?.medicaoTermino != null && errosDaLinha(store, store.linhas.length - 1).length === 0)

  function nova() {
    const l = store.addLinha()
    setEditando({ linha: l, indice: store.linhas.length })
  }

  return (
    <>
      {store.linhas.length === 0 ? (
        <div className="card-surface-variant px-4 py-8 text-center">
          <p className="text-on-surface font-medium">Nenhuma linha lançada neste dia</p>
          <p className="text-sm text-on-surface-dim mt-1">Toque em + Linha para preencher obra, serviço, término e horas.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {store.linhas.map((l, i) => {
            const erros = errosDaLinha(store, i)
            const dif = diferencaLinha(store, i)
            const destacada = store.rejeicao?.linhaId === l.id
            return (
              <li key={l.id} className={`card-surface flex items-stretch overflow-hidden ${erros.length > 0 || destacada ? 'border border-bad/70' : ''}`}>
                <button type="button" onClick={() => setEditando({ linha: l, indice: i })} className="flex-1 p-4 text-left min-w-0">
                  <div className="flex items-center gap-2">
                    {erros.length > 0 || destacada ? (
                      <AlertTriangle className="h-4 w-4 text-bad shrink-0" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-ok shrink-0" />
                    )}
                    <p className="text-on-surface font-medium truncate">{l.servico?.nome ?? '(sem serviço)'}</p>
                    <span className="text-[10px] uppercase tracking-wide bg-accent/15 text-accent px-1.5 py-0.5 rounded ml-auto shrink-0">
                      {papelDaLinha(store, i) === 'PRINCIPAL' ? 'principal' : `fração ${i + 1}`}
                    </span>
                  </div>
                  <p className="text-xs text-on-surface-dim mt-1 truncate">{l.obraNome || '(sem obra)'}{l.equipeNome ? ` · ${l.equipeNome}` : ''}</p>
                  <p className="text-xs text-on-surface mt-1 font-mono tabular">
                    {formulario === 'P' ? '' : `${inicios[i] != null ? fmtNum(inicios[i], 0) : '?'} → `}
                    {l.medicaoTermino != null ? fmtNum(l.medicaoTermino, formulario === 'P' ? 2 : 0) : '—'}
                    {dif != null ? ` (dif ${fmtNum(dif, formulario === 'P' ? 2 : 0)})` : ''}
                    {' · '}
                    {l.hInicio ?? '—'}–{l.hFim ?? '—'}
                  </p>
                  {erros[0] && <p className="text-xs text-bad mt-1">{erros[0].mensagem}</p>}
                  {destacada && store.rejeicao && <p className="text-xs text-bad mt-1">{store.rejeicao.message}</p>}
                </button>
                <button
                  type="button"
                  onClick={() => confirm('Remover esta linha?') && store.removeLinha(l.id)}
                  className="px-3 text-bad hover:bg-bad/10 transition-colors flex items-center"
                  aria-label="Remover linha"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <Button variant="outline" size="lg" onClick={nova} disabled={!podeFracionar} className="w-full flex items-center gap-2">
        <Plus className="h-5 w-5" />
        {store.linhas.length === 0 ? 'Linha' : 'Fração (mesmo dia)'}
      </Button>
      {store.linhas.length > 0 && !podeFracionar && (
        <p className="text-xs text-on-surface-dim text-center">Complete a linha anterior (com término) para fracionar o dia.</p>
      )}

      {editando && (
        <LinhaMveFormSheet
          state={store}
          indice={editando.indice}
          initial={editando.linha}
          destaque={store.rejeicao?.linhaId === editando.linha.id ? { campo: store.rejeicao.campo, mensagem: store.rejeicao.message } : null}
          onSave={(l) => {
            store.updateLinha(l)
            if (store.rejeicao?.linhaId === l.id) store.limparRejeicao()
            setEditando(null)
          }}
          onDismiss={() => {
            // Linha recém-criada e vazia é descartada ao cancelar
            const l = store.linhas[editando.indice]
            if (l && l.medicaoTermino == null && l.servico == null && l.obraId == null) store.removeLinha(l.id)
            setEditando(null)
          }}
        />
      )}
    </>
  )
}

// ============================================================================
//  EPP — período
// ============================================================================

function PeriodoEpp() {
  const store = useParteDiariaStore()
  const ctx = contextoEpp(store)
  const [picker, setPicker] = useState<'obra' | 'servico' | null>(null)
  const obrasOnline = useObrasParteDiaria(!ctx)
  const obras: ObraParteDto[] = ctx?.obras ?? obrasOnline.items
  const servicos = useServicosParteDiaria(store.mveId, store.epp.obraId)
  const erros = errosEpp(store)
  const erroDe = (campo: CampoEpp) => erros.find((e) => e.campo === campo)?.mensagem ?? null
  const destacado = (campo: string) => store.rejeicao?.campo === campo
  const previa = previaValorEpp(store)
  const mes = store.dt.slice(0, 7)

  return (
    <>
      <Section title="PERÍODO">
        <PickerField
          label="Obra"
          value={store.epp.obraNome || 'Selecionar…'}
          onClick={() => setPicker('obra')}
          erro={erroDe('obra')}
          destacado={destacado('obra')}
        />
        <PickerField
          label="Serviço"
          value={store.epp.servico?.nome ?? 'Selecionar…'}
          sublabel={store.epp.servico?.isento ? 'isento (não paga)' : null}
          onClick={() => store.epp.obraId != null && setPicker('servico')}
          disabled={store.epp.obraId == null}
          erro={erroDe('servico')}
          destacado={destacado('servico')}
        />
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput
            label="Data inicial"
            type="date"
            value={store.epp.dtInicial ?? ''}
            onChange={(v) => store.setEpp({ dtInicial: v || null })}
            min={`${mes}-01`}
            max={store.dt}
            erro={erroDe('dtInicial')}
            destacado={destacado('dtInicial')}
          />
          <LabeledInput
            label="Data final"
            type="date"
            value={store.epp.dtFinal ?? ''}
            onChange={(v) => store.setEpp({ dtFinal: v || null })}
            min={`${mes}-01`}
            max={store.dt}
            erro={erroDe('dtFinal')}
            destacado={destacado('dtFinal')}
          />
        </div>
        <LabeledInput label="Observação" value={store.epp.observacao ?? ''} onChange={(v) => store.setEpp({ observacao: v || null })} maxLength={500} />
      </Section>

      <div className="card-surface-variant px-4 py-4 grid grid-cols-3 gap-3 text-center">
        <Stat label="Dias" value={store.epp.dtInicial && store.epp.dtFinal && store.epp.dtInicial <= store.epp.dtFinal ? String(diasInclusivos(store.epp.dtInicial, store.epp.dtFinal)) : '—'} />
        <Stat label="Valor estimado" value={previa ? fmtBRL(previa.valor) : '—'} />
        <Stat label="Mês (estim.)" value={previa ? fmtBRL(previa.valorMes) : '—'} />
      </div>
      <p className="text-xs text-on-surface-dim px-1">
        Prévia com o preço do contrato em cache. O valor oficial sai do “Recalcular medição” do SIGA.
      </p>

      {ctx && ctx.linhasMes.length > 0 && (
        <Section title="JÁ LANÇADOS NO MÊS">
          {ctx.linhasMes.map((l) => (
            <div key={l.sigaEppId} className="card-surface-variant px-4 py-2 flex justify-between text-sm">
              <span className="text-on-surface">
                {dataBR(l.dtInicial)} a {dataBR(l.dtFinal)}
              </span>
              <span className="text-on-surface-dim">{l.servicoNome ?? ''}</span>
            </div>
          ))}
        </Section>
      )}

      {picker === 'obra' && (
        <PickerSheet<ObraParteDto>
          open
          title="Obra"
          items={obras}
          label={(o) => o.codigo}
          sublabel={(o) => o.nome}
          onPick={(o) => {
            store.setEpp({ obraId: o.id, obraNome: `${o.codigo}${o.nome ? ` · ${o.nome}` : ''}`, servico: null })
            setPicker(null)
          }}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar obra"
          emptyMessage={obrasOnline.loading ? 'Carregando obras…' : 'Nenhuma obra ativa para o chefe de equipe'}
        />
      )}
      {picker === 'servico' && (
        <PickerSheet<ServicoParteDto>
          open
          title="Serviço executado"
          items={servicos.items}
          label={(s) => s.nome}
          sublabel={(s) => [s.grupoNome, s.isento ? 'isento' : null].filter(Boolean).join(' · ') || null}
          onPick={(s) => {
            store.setEpp({ servico: s })
            setPicker(null)
          }}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar serviço"
          emptyMessage={servicos.loading ? 'Carregando serviços…' : 'Nenhum serviço para este equipamento e obra'}
        />
      )}
    </>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-on-surface-dim uppercase">{label}</p>
      <p className="text-lg font-black text-accent mt-1 truncate">{value}</p>
    </div>
  )
}
